// Module 1 — Onglet Historique (livraison 2) : une seule frise, en langage courant, et la
// « Situation au… » d'une date passée. Les annulations et corrections restent lisibles.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { History, CalendarSearch, Ban, Pencil, Undo2, CircleDot, Lock } from 'lucide-react';
import { Carte, Badge, Bouton, BandeauInfo, EtatVide, Squelette, Champ, Grille, formaterDate } from '../../design/composants';

const FAMILLES = [
  { cle: 'tous', libelle: 'Tout' },
  { cle: 'affectation', libelle: 'Affectations et postes' },
  { cle: 'contrat', libelle: 'Contrat' },
  { cle: 'statut', libelle: 'Statut et sortie' },
  { cle: 'organisation', libelle: 'Responsable et métier' },
  { cle: 'carriere', libelle: 'Carrière' },
  { cle: 'salaire', libelle: 'Évolutions salariales' },
  { cle: 'correction', libelle: 'Corrections' },
  { cle: 'discipline', libelle: 'Discipline' },
  { cle: 'formation', libelle: 'Formations' },
];
const TON = { affectation: 'info', contrat: 'neutre', statut: 'alerte', organisation: 'neutre', carriere: 'succes', salaire: 'neutre', correction: 'neutre', discipline: 'danger', formation: 'neutre', entree: 'succes' };

function SituationAu({ salarieId }) {
  const [date, setDate] = useState('');
  const [s, setS] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    if (!date) { setS(null); return undefined; }
    let actif = true;
    axios.get(`/api/salaries/${salarieId}/situation`, { params: { date } })
      .then(({ data }) => { if (actif) { setS(data); setErreur(''); } })
      .catch((e) => { if (actif) setErreur(e.response?.data?.message || 'Situation indisponible.'); });
    return () => { actif = false; };
  }, [date, salarieId]);
  return (
    <Carte titre="Situation au…" Icone={CalendarSearch}
      action={<input type="date" className="rh-champ-saisie" style={{ width: 180 }} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date de la situation" max={new Date().toISOString().slice(0, 10)} />}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!date && <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-texte-3)' }}>Choisissez une date pour voir où travaillait le salarié, sur quel poste, avec quel responsable et quel contrat.</p>}
      {s && !s.present && <p style={{ margin: 0, fontSize: 13 }}>{s.message}</p>}
      {s && s.present && (
        <>
          <Grille>
            <Champ libelle="Statut" valeur={s.statut} />
            <Champ libelle="Site" valeur={s.affectation?.site} />
            <Champ libelle="Service" valeur={s.affectation?.service || s.affectation?.departement} />
            <Champ libelle="Poste" valeur={s.poste || 'Aucun poste de responsabilité'} />
            <Champ libelle="Métier" valeur={s.metier} />
            <Champ libelle="Responsable" valeur={s.responsable ? `${s.responsable.nom || 'Poste vacant'} (${s.responsable.poste})` : 'Aucun'} />
            <Champ libelle="Contrat" valeur={s.contrat ? `${s.contrat.type} depuis le ${formaterDate(s.contrat.debut)}${s.contrat.fin ? ` jusqu'au ${formaterDate(s.contrat.fin)}` : ''}${s.contrat.en_cours ? '' : ' (échu à cette date)'}` : null} />
          </Grille>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--sg-texte-4)' }}>{s.note}</p>
        </>
      )}
    </Carte>
  );
}

export default function HistoriqueSalarie({ salarieId, peutAnnuler, rafraichir, onAnnuler, onCorrigerAffectation, onAnnulerAffectation, onLeverSanction }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [famille, setFamille] = useState('tous');
  useEffect(() => {
    let actif = true;
    axios.get(`/api/salaries/${salarieId}/historique`)
      .then(({ data }) => { if (actif) setDonnees(data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.message || 'L\'historique n\'a pas pu être chargé.'); });
    return () => { actif = false; };
  }, [salarieId, rafraichir]);

  const presentes = useMemo(() => new Set((donnees?.evenements || []).map((e) => (e.famille === 'entree' ? 'affectation' : e.famille))), [donnees]);
  const visibles = (donnees?.evenements || []).filter((e) => famille === 'tous' || e.famille === famille || (famille === 'affectation' && e.famille === 'entree'));
  // Une Map garde l'ordre chronologique inverse (un objet trierait les années en ordre croissant).
  const parAnnee = visibles.reduce((acc, e) => { const a = (e.date || '').slice(0, 4) || 'Sans date'; if (!acc.has(a)) acc.set(a, []); acc.get(a).push(e); return acc; }, new Map());

  if (erreur) return <BandeauInfo ton="danger">{erreur}</BandeauInfo>;
  return (
    <div>
      <SituationAu salarieId={salarieId} />
      <Carte titre="Historique" Icone={History}>
        {!donnees ? <><Squelette largeur="60%" /><Squelette largeur="40%" style={{ marginTop: 10 }} /></> : (
          <>
            <div className="rh-vues rh-vues-ligne" role="group" aria-label="Filtrer l'historique">
              {FAMILLES.filter((f) => f.cle === 'tous' || presentes.has(f.cle)).map((f) => (
                <button key={f.cle} type="button" className="rh-vue" aria-pressed={famille === f.cle} onClick={() => setFamille(f.cle)}>{f.libelle}</button>
              ))}
            </div>
            {visibles.length === 0 ? <EtatVide Icone={History} titre="Aucun événement" texte="Aucun événement de ce type n'est enregistré." /> : [...parAnnee.entries()].map(([annee, evs]) => (
              <section key={annee} style={{ marginBottom: 8 }}>
                <h3 style={{ fontSize: 13, color: 'var(--sg-texte-3)', fontWeight: 600, margin: '14px 0 4px' }}>{annee}</h3>
                <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: '2px solid var(--sg-bord)' }}>
                  {evs.map((e) => {
                    const annule = e.etat === 'annule';
                    return (
                      <li key={e.id} style={{ position: 'relative', padding: '10px 0 10px 18px', opacity: annule ? 0.6 : 1 }}>
                        <CircleDot size={14} color={annule ? 'var(--sg-texte-4)' : 'var(--sg-primaire)'} style={{ position: 'absolute', left: -8, top: 13, background: 'var(--sg-surface)' }} aria-hidden="true" />
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{e.date_longue}</span>
                          <Badge ton={TON[e.famille] || 'neutre'}>{e.titre}</Badge>
                          {e.confidentiel && <Badge ton="neutre"><Lock size={11} aria-hidden="true" />Confidentiel</Badge>}
                          {annule && <Badge ton="danger"><Ban size={11} aria-hidden="true" />Annulé</Badge>}
                          {e.etat === 'programme' && <Badge ton="alerte">Programmé</Badge>}
                          {e.etat === 'corrige' && <Badge ton="info">Corrigé</Badge>}
                        </div>
                        <div style={{ fontSize: 14, textDecoration: annule ? 'line-through' : 'none' }}>{e.phrase}</div>
                        {(e.motif || e.auteur) && <div className="rh-cellule-secondaire">{e.motif ? `Motif : ${e.motif}` : ''}{e.motif && e.auteur ? ' · ' : ''}{e.auteur ? `Saisi par ${e.auteur}` : ''}</div>}
                        {annule && e.annulation && <div className="rh-cellule-secondaire">Annulé{e.annulation.le ? ` le ${formaterDate(e.annulation.le)}` : ''}{e.annulation.par ? ` par ${e.annulation.par}` : ''}{e.annulation.motif ? ` — motif : ${e.annulation.motif}` : ''}</div>}
                        {(e.corrections || []).map((c) => <div key={c.le} className="rh-cellule-secondaire">{c.texte}</div>)}
                        {!annule && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                            {e.source?.type === 'affectation' && e.source.statut !== 'ANNULEE' && onCorrigerAffectation && <Bouton petit variante="discret" Icone={Pencil} onClick={() => onCorrigerAffectation(e.source.id)}>Corriger</Bouton>}
                            {e.source?.type === 'affectation' && e.source.statut !== 'ANNULEE' && onAnnulerAffectation && <Bouton petit variante="discret" Icone={Undo2} onClick={() => onAnnulerAffectation(e.source.id)}>Annuler</Bouton>}
                            {peutAnnuler && e.source?.type === 'contrat' && e.source.annulable && <Bouton petit variante="discret" Icone={Undo2} onClick={() => onAnnuler({ type: 'contrat', id: e.source.id, titre: e.titre })}>Annuler cette modification</Bouton>}
                            {peutAnnuler && e.source?.type === 'evenement' && e.source.annulable && <Bouton petit variante="discret" Icone={Undo2} onClick={() => onAnnuler({ type: 'evenement', id: e.source.id, titre: e.titre })}>Annuler</Bouton>}
                            {e.source?.type === 'sanction' && e.source.statut === 'active' && onLeverSanction && <Bouton petit variante="discret" onClick={() => onLeverSanction(e.source.id)}>Lever la sanction</Bouton>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
            {!donnees.salarial_visible && <p style={{ fontSize: 12, color: 'var(--sg-texte-4)', margin: '12px 0 0' }}>Les événements purement salariaux et disciplinaires ne sont visibles que du service RH, de la paie (pour le salarial) et du salarié lui-même.</p>}
          </>
        )}
      </Carte>
    </div>
  );
}
